import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@components/ui/Logo";
import arrowDownIcon from "@assets/images/arrow-down-s-line1.svg";
import dropdownBadge from "@assets/images/Group-1410087795.svg";
import useOutsideClick from "@/hooks/useOutsideClick";

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href: string;
  children?: NavChild[];
}

const userTypes = [
  { label: "Personal", href: "/residential", id: "personal" },
  { label: "Business", href: "/business", id: "business" },
  { label: "Partnership", href: "/partners", id: "partnership" },
];

const navItems: NavItem[] = [
  {
    label: "Services",
    href: "/services",
    children: [
      { label: "Online Service/Pay Bill", href: "/services/pay-bill" },
      { label: "New Connections (Residential)", href: "/services/new-connections" },
      { label: "Gas Services", href: "/services/gas-services" },
      { label: "Residential Gas Solutions", href: "/services/gas-solutions" },
      { label: "Billing & Payment Solutions", href: "/services/billing-payment" },
    ],
  },
  { label: "Tariffs & Charges", href: "/tariffs" },
  {
    label: "About",
    href: "/about",
    children: [
      { label: "About Us", href: "/about" },
      { label: "Why Choose GASIS", href: "/about/why-choose-gasis" },
      { label: "Careers", href: "/careers" },
      { label: "Sustainability", href: "/sustainability" },
      { label: "News & Updates", href: "/news" },
    ],
  },
  {
    label: "Safety & Support",
    href: "/safety",
    children: [
      { label: "Safety & Emergency", href: "/safety" },
      { label: "Safety Guidelines & Procedures", href: "/safety/guidelines" },
      { label: "FAQs", href: "/faq" },
      { label: "Customer Feedback", href: "/customer-feedback" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
];

export function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownRef = useOutsideClick(() => setOpenDropdown(null));

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    const body = document.querySelector("body");
    if (mobileMenuOpen) {
      body?.classList.add("overflow-hidden");
    } else {
      body?.classList.remove("overflow-hidden");
    }
  }, [mobileMenuOpen]);

  const getCurrentUserType = () => {
    if (location.pathname.startsWith("/business")) return "business";
    if (location.pathname.startsWith("/partners")) return "partnership";
    return "personal";
  };

  const currentUserType = getCurrentUserType();

  // Check if a nav item or any of its children match the current route
  const isNavActive = (item: NavItem) => {
    if (location.pathname === item.href) return true;
    if (item.children?.some((child) => location.pathname === child.href))
      return true;
    // Partial match for parent paths (e.g. /services matches /services/*)
    if (
      item.href !== "/" &&
      location.pathname.startsWith(item.href) &&
      !item.children
    )
      return true;
    return false;
  };

  return (
    <header className="w-full sticky top-0 z-[99]">
      <nav className="w-full flex items-center justify-between gap-4 lg:gap-6 xl:gap-10 h-[57px]">
        {/* Left side - Logo and User Type Switcher */}
        <div className="flex items-center gap-3 md:gap-5">
          <Logo />

          {/* User Type Switcher - Desktop (pill style) */}
          <div className="hidden md:flex items-center bg-white/10 rounded-full p-[3px] backdrop-blur-sm">
            {userTypes.map((type) => (
              <Link
                key={type.id}
                to={type.href}
                className={`px-3 lg:px-4 py-1 lg:py-1.5 rounded-full text-[12px] lg:text-[14px] tracking-[-0.02em] font-medium transition-all duration-200 whitespace-nowrap ${
                  currentUserType === type.id
                    ? "bg-white text-[#122d63] shadow-sm"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {type.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Right side - Navigation and Login */}
        <div className="hidden lg:flex items-center gap-3 xl:gap-5">
          {/* Navigation Links */}
          <div ref={dropdownRef} className="flex items-center gap-0.5 xl:gap-1">
            {navItems.map((item) => {
              const active = isNavActive(item);
              return (
                <div key={item.href} className="relative">
                  {item.children ? (
                    <button
                      onClick={() =>
                        setOpenDropdown((prev) =>
                          prev === item.label ? null : item.label
                        )
                      }
                      className={`flex items-center px-2 xl:px-3 py-1.5 rounded-full text-[15px] xl:text-[18px] whitespace-nowrap transition-all duration-200 ${
                        active
                          ? "text-white bg-white/15"
                          : "text-[#f8f8f8] hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {item.label}
                      <img
                        src={arrowDownIcon}
                        alt=""
                        className={`w-5 h-5 xl:w-6 xl:h-6 transition-transform duration-200 ${
                          openDropdown === item.label ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center px-2 xl:px-3 py-1.5 rounded-full text-[15px] xl:text-[18px] whitespace-nowrap transition-all duration-200 ${
                        active
                          ? "text-white bg-white/15"
                          : "text-[#f8f8f8] hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}

                  {/* Desktop Dropdown */}
                  {item.children && openDropdown === item.label && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                      {/* Pointer */}
                      <svg
                        width="20"
                        height="10"
                        viewBox="0 0 20 10"
                        className="absolute -top-[3px] left-1/2 -translate-x-1/2 z-10"
                      >
                        <path d="M0 10L10 0L20 10H0Z" fill="white" />
                      </svg>

                      <div className="flex bg-white shadow shadow-black/30 rounded-[12px] overflow-hidden">
                        {/* Links */}
                        <div className="p-6 xl:p-10 w-[260px] xl:w-[300px]">
                          <h3 className="text-xs text-[#0A0E2EBF] font-semibold font-albert-sans">
                            {item.label}
                          </h3>
                          <div className="flex flex-col gap-3 mt-3">
                            {item.children.map((child) => {
                              const childActive =
                                location.pathname === child.href;
                              return (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  className={`text-[13px] xl:text-[14px] leading-tight w-fit font-albert-sans transition-colors ${
                                    childActive
                                      ? "text-[#f24d2b] font-semibold"
                                      : "text-[#0A0E2E] hover:text-[#f24d2b] hover:underline"
                                  }`}
                                >
                                  {child.label}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                        {/* Badge illustration */}
                        <div className="w-[180px] xl:w-[212px] bg-[#F1F1F1] p-5 grid place-content-center">
                          <img
                            src={dropdownBadge}
                            alt=""
                            className="w-[80px] xl:w-[100px] h-auto object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Login Button */}
          <Link
            to="/login"
            className="py-2 xl:py-2.5 px-4 xl:px-5 bg-[#f8f8f8] rounded-[40px] text-sm xl:text-base text-[#0a0e2e] hover:bg-[#dedede] transition-colors whitespace-nowrap"
          >
            Login
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden flex flex-col items-center justify-center w-10 h-10 gap-1.5"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={`w-6 h-0.5 bg-white transition-transform ${mobileMenuOpen ? "rotate-45 translate-y-2" : ""}`}
          />
          <span
            className={`w-6 h-0.5 bg-white transition-opacity ${mobileMenuOpen ? "opacity-0" : ""}`}
          />
          <span
            className={`w-6 h-0.5 bg-white transition-transform ${mobileMenuOpen ? "-rotate-45 -translate-y-2" : ""}`}
          />
        </button>
      </nav>

      {/* Mobile Menu - Slide-out Panel */}
      <div
        className={`fixed inset-0 z-[999] flex justify-end bg-black/50 backdrop-blur-lg transition-all duration-300 lg:hidden ${mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div
          className={`w-full sm:w-[400px] h-full px-5 py-5 bg-[#f24d2b] overflow-y-auto pb-10 transition-transform duration-300 ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between gap-4">
            <Logo />
            <button
              className="flex flex-col items-center justify-center w-10 h-10 gap-1.5"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <span className="w-6 h-0.5 bg-white rotate-45 translate-y-2" />
              <span className="w-6 h-0.5 bg-white opacity-0" />
              <span className="w-6 h-0.5 bg-white -rotate-45 -translate-y-2" />
            </button>
          </div>

          {/* User Type Switcher - Mobile (pill style) */}
          <div className="flex items-center gap-1 bg-white/15 rounded-full p-[3px] mt-6 mb-6 md:mt-8 md:mb-8">
            {userTypes.map((type) => (
              <Link
                key={type.id}
                to={type.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex-1 text-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-full text-[12px] sm:text-[13px] md:text-[14px] font-medium transition-all duration-200 ${
                  currentUserType === type.id
                    ? "bg-white text-[#122d63] shadow-sm"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {type.label}
              </Link>
            ))}
          </div>

          {/* Navigation Links - Mobile */}
          <div className="flex flex-col gap-1 mb-6">
            {navItems.map((item) => {
              const active = isNavActive(item);
              return (
                <div key={item.href}>
                  {item.children ? (
                    <>
                      <button
                        className={`flex items-center justify-between w-full gap-2 px-3 py-2.5 rounded-lg transition-colors ${
                          active ? "bg-white/15" : ""
                        }`}
                        onClick={() =>
                          setMobileExpanded(
                            mobileExpanded === item.label ? null : item.label
                          )
                        }
                      >
                        <span className="text-[14px] sm:text-sm md:text-base text-white font-medium">
                          {item.label}
                        </span>
                        <img
                          src={arrowDownIcon}
                          alt=""
                          className={`w-6 h-6 sm:w-8 sm:h-8 transition-transform duration-200 ${
                            mobileExpanded === item.label ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`grid transition-all duration-300 ease-in-out ${
                          mobileExpanded === item.label
                            ? "grid-rows-[1fr] opacity-100 mt-1"
                            : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="flex flex-col gap-1 pl-3">
                            {item.children.map((child) => {
                              const childActive =
                                location.pathname === child.href;
                              return (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={`text-[13px] sm:text-xs md:text-sm leading-tight px-3 py-2 rounded-lg transition-colors ${
                                    childActive
                                      ? "bg-white/20 text-white font-semibold"
                                      : "text-white/80 hover:text-white hover:bg-white/10"
                                  }`}
                                >
                                  {child.label}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block text-[14px] sm:text-sm md:text-base font-medium px-3 py-2.5 rounded-lg transition-colors ${
                        active
                          ? "text-white bg-white/15"
                          : "text-white hover:bg-white/10"
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          {/* Login Button - Mobile */}
          <Link
            to="/login"
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full py-2.5 sm:py-3 px-6 bg-[#f8f8f8] rounded-[40px] text-[13px] sm:text-sm text-[#0a0e2e] text-center font-medium"
          >
            Login
          </Link>
        </div>
      </div>
    </header>
  );
}
